import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import AxiosInstance from "../../services/AxiosInstance";
import { SMTP_URL } from "../../Constants/Constants";
import { useAlert } from "./../alerts/AlertContext";
import { fetchData } from "../../services/UtilService";

// Styled components for better visual hierarchy
const FormSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },

}));

function Smtp() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showAlert = useAlert();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    host: "",
    displayName: "",
    port: "",
    applicationName: "",
    clientSecret: "",
    clientValue: "",
    directoryName: "",
    scope: "",
    authority: "",
    notes: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({
    host: false,
    displayName: false,
    port: false,
    applicationName: false,
    clientSecret: false,
    clientValue: false,
    directoryName: false,
    scope: false,
    authority: false,
    notes: false,
  });

  useEffect(() => {
    loadSMTP();
  }, []);

  const loadSMTP = async () => {
    setLoading(true);
    try {
      const data = await fetchData(SMTP_URL);
      if (data.length > 0) {
        const smtpData = data[0];
        setFormData({
          host: smtpData.host || "",
          displayName: smtpData.displayName || "",
          port: smtpData.port || "",
          applicationName: smtpData.applicationName || "",
          clientSecret: smtpData.clientSecret || "",
          clientValue: smtpData.clientValue || "",
          directoryName: smtpData.directoryName || "",
          scope: smtpData.scope || "",
          authority: smtpData.authority || "",
          notes: smtpData.notes || "",
        });
        
        // Initialize touched state for all fields when data is loaded
        setTouched({
          host: false,
          displayName: false,
          port: false,
          applicationName: false,
          clientSecret: false,
          clientValue: false,
          directoryName: false,
          scope: false,
          authority: false,
          notes: false,
        });
        
        // Clear any existing errors
        setFormErrors({});
      }
    } catch (error) {
      showAlert("Failed to load SMTP configuration", "error");
      console.error("Failed to load SMTP data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Validate field immediately and show error
    const error = validateField(field, value);
    setFormErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'host':
        return !value.trim() ? 'Host is required' : '';
      case 'port':
        if (!value.trim()) return 'Port is required';
        if (isNaN(value) || parseInt(value) < 1 || parseInt(value) > 65535) {
          return 'Port must be a valid number between 1-65535';
        }
        return '';
      case 'displayName':
        return !value.trim() ? 'Display Name is required' : '';
      case 'applicationName':
        return !value.trim() ? 'Application Name is required' : '';
      case 'clientSecret':
        return !value.trim() ? 'Client Secret is required' : '';
      case 'clientValue':
        return !value.trim() ? 'Client Value is required' : '';
      case 'directoryName':
        return !value.trim() ? 'Directory Name is required' : '';
      case 'scope':
        return !value.trim() ? 'Scope is required' : '';
      case 'authority':
        return !value.trim() ? 'Authority is required' : '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        // Mark all fields as touched when validation fails
        setTouched(prev => ({
          ...prev,
          [field]: true
        }));
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showAlert("Please fix the errors before saving", "warning");
      return;
    }

    setSaving(true);
    try {
      const smtpData = { ...formData };
      
      if (smtpData.id) {
        // Update existing SMTP
        await AxiosInstance.put(`${SMTP_URL}/${smtpData.id}`, smtpData);
        showAlert("SMTP configuration updated successfully!", "success");
      } else {
        // Create new SMTP
        await AxiosInstance.post(SMTP_URL, smtpData);
        showAlert("SMTP configuration created successfully!", "success");
      }
      
      await loadSMTP(); // Reload data to get the ID if it's a new record
    } catch (error) {
      showAlert("Failed to save SMTP configuration", "error");
      console.error("Error saving SMTP:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadSMTP();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, maxWidth: 1200, mx: 'auto' }}>

      {/* Server Configuration Section */}
      <FormSection elevation={2}>
        <SectionHeader>
          <SettingsIcon />
          <Typography variant="h6" component="h2">
            Server Configuration
          </Typography>
        </SectionHeader>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Host"
              name="host"
              value={formData.host}
              onChange={(e) => handleInputChange('host', e.target.value)}
              error={!!formErrors.host && touched.host}
              helperText={touched.host ? formErrors.host : ''}
              placeholder="smtp.example.com"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Port"
              name="port"
              value={formData.port}
              onChange={(e) => handleInputChange('port', e.target.value)}
              error={!!formErrors.port && touched.port}
              helperText={touched.port ? formErrors.port : ''}
              placeholder="587"
              type="number"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Chip label="Port" size="small" variant="outlined" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              error={!!formErrors.displayName && touched.displayName}
              helperText={touched.displayName ? formErrors.displayName : ''}
              placeholder="Your Company Name"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Application Name"
              name="applicationName"
              value={formData.applicationName}
              onChange={(e) => handleInputChange('applicationName', e.target.value)}
              error={!!formErrors.applicationName && touched.applicationName}
              helperText={touched.applicationName ? formErrors.applicationName : ''}
              placeholder="My Application"
              size="small"
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Authentication Section */}
      <FormSection elevation={2}>
        <SectionHeader>
          <SecurityIcon />
          <Typography variant="h6" component="h2">
            Authentication & Security
          </Typography>
        </SectionHeader>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Client Secret"
              name="clientSecret"
              value={formData.clientSecret}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
              error={!!formErrors.clientSecret && touched.clientSecret}
              helperText={touched.clientSecret ? formErrors.clientSecret : ''}
              type="password"
              placeholder="Enter client secret"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Client Value"
              name="clientValue"
              value={formData.clientValue}
              onChange={(e) => handleInputChange('clientValue', e.target.value)}
              error={!!formErrors.clientValue && touched.clientValue}
              helperText={touched.clientValue ? formErrors.clientValue : ''}
              placeholder="Enter client value"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Directory Name"
              name="directoryName"
              value={formData.directoryName}
              onChange={(e) => handleInputChange('directoryName', e.target.value)}
              error={!!formErrors.directoryName && touched.directoryName}
              helperText={touched.directoryName ? formErrors.directoryName : ''}
              placeholder="Directory name"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Scope"
              name="scope"
              value={formData.scope}
              onChange={(e) => handleInputChange('scope', e.target.value)}
              error={!!formErrors.scope && touched.scope}
              helperText={touched.scope ? formErrors.scope : ''}
              placeholder="API scope"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Authority"
              name="authority"
              value={formData.authority}
              onChange={(e) => handleInputChange('authority', e.target.value)}
              error={!!formErrors.authority && touched.authority}
              helperText={touched.authority ? formErrors.authority : ''}
              placeholder="https://login.microsoftonline.com/your-tenant-id"
              size="small"
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Notes Section */}
      <FormSection elevation={2}>
        <SectionHeader>
          <EmailIcon />
          <Typography variant="h6" component="h2">
            Additional Information
          </Typography>
        </SectionHeader>
        
        <StyledTextField
          fullWidth
          label="Notes"
          name="notes"
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          multiline
          rows={2}
          placeholder="Add any additional notes or comments about this SMTP configuration..."
          helperText="Optional notes for documentation purposes"
          size="small"
        />
      </FormSection>

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2, 
        flexWrap: 'wrap',
        mt: 2,
        mb: 1,
      }}>

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="medium"
      
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>


    </Box>
  );
}

export default Smtp;
